import { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { Row } from '@/components/SettingsLayout';
import { Text, Icon, IconType } from '@/ui';
import ImageWithFallback from '@/ui/ImageWithFallBack/ImageWithFallBack';
import { formatCurrency } from '@/utils/currency';
import { residualsDateFormatter } from '@/utils/helpers/dateFormater';
import classNames from 'classnames';
import styles from '../styles.module.css';
import accruingStatusIcon from '@/assets/icons/accruingStatus.svg';
import dueStatusIcon from '@/assets/icons/dueStatus.svg';
import paidStatusIcon from '@/assets/icons/paidStatus.svg';
import { DataTable } from '@/components/data-table';
import type { CardField } from '@/components/data-table';

type ResidualReport = {
  _id: string;
  feesReportId?: string;
  name?: string;
  logo?: string;
  residualsEarned?: number;
  status?: string;
  datePaid?: string | Date;
  startDate?: string | Date;
  endDate?: string | Date;
};

type Props = {
  data: ResidualReport[];
  sortBy: string | null;
  sortOrder: 'asc' | 'desc' | null;
  onSort: (newSortBy: string | null, newSortOrder: 'asc' | 'desc' | null) => void;
};

const getStatusIcon = (status?: string) => {
  switch (status?.toLowerCase()) {
    case 'paid':
      return paidStatusIcon;
    case 'due':
      return dueStatusIcon;
    case 'accruing':
    default:
      return accruingStatusIcon;
  }
};

function SortableHeader({
  label,
  field,
  sortBy,
  sortOrder,
  onSort,
}: {
  label: string;
  field: string;
  sortBy: string | null;
  sortOrder: 'asc' | 'desc' | null;
  onSort: (field: string) => void;
}) {
  const iconClass = classNames(styles.sortIcon, {
    [styles.filterNone]: sortBy !== field,
    [styles.filterAsc]: sortBy === field && sortOrder === 'asc',
    [styles.filterDesc]: sortBy === field && sortOrder === 'desc',
  });
  return (
    <Row gapSize='none' align='center'>
      <Text size='s' weight='semi-bold'>
        {label}
      </Text>
      <button
        type='button'
        className={iconClass}
        onClick={(e) => {
          e.stopPropagation();
          onSort(field);
        }}
        style={{
          cursor: 'pointer',
          marginLeft: '8px',
          display: 'flex',
          alignItems: 'center',
          background: 'none',
          border: 'none',
          padding: 0,
        }}
        aria-label={`Sort by ${label}`}
      >
        <Icon width={16} height={16} fill='transparent' type={IconType.ArrowsSplit} />
      </button>
    </Row>
  );
}

function FinalUserResidualsTable({ data, sortBy, sortOrder, onSort }: Props) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const handleSort = useCallback(
    (field: string) => {
      let newSortBy: string | null = field;
      let newSortOrder: 'asc' | 'desc' | null = 'asc';
      if (sortBy === field) {
        if (sortOrder === 'asc') newSortOrder = 'desc';
        else if (sortOrder === 'desc') {
          newSortBy = null;
          newSortOrder = null;
        }
      }
      onSort(newSortBy, newSortOrder);
    },
    [sortBy, sortOrder, onSort],
  );

  const columnHelper = createColumnHelper<ResidualReport>();
  const mobileCardFields = useMemo<CardField<ResidualReport>[]>(
    () => [
      { key: 'name', primary: true, render: (row) => row.original.name || '-' },
      {
        key: 'residualsEarned',
        label: t('Residuals.residuals_earned'),
        render: (row) => formatCurrency(row.original.residualsEarned ?? 0, 'USD'),
      },
      {
        key: 'status',
        label: t('Residuals.status'),
        render: (row) => {
          const s = (row.original.status ?? '').toLowerCase();
          if (s === 'paid') return t('Residuals.status_paid') ?? 'Paid';
          if (s === 'due') return t('Residuals.status_due') ?? 'Due';
          return t('Residuals.status_accruing') ?? 'Accruing';
        },
      },
      {
        key: 'endDate',
        label: t('Residuals.pay_date'),
        render: (row) => residualsDateFormatter(row.original.endDate ?? '-'),
      },
      {
        key: 'datePaid',
        label: t('Residuals.paid_date'),
        render: (row) => residualsDateFormatter(row.original.datePaid ?? '-'),
      },
    ],
    [t],
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor((r) => r.name ?? '-', {
        id: 'name',
        header: () => (
          <Row gapSize='none'>
            <Text size='s' weight='semi-bold'>
              {t('Residuals.organization_name')}
            </Text>
          </Row>
        ),
        cell: ({ row }) => (
          <Row inlineRow>
            <ImageWithFallback
              src={row.original.logo}
              placeholderSrc='/placeholder.svg'
              thumbnail
              width={48}
              height={48}
            />
            <Text size='s' weight='light-medium'>
              {row.original.name || '-'}
            </Text>
          </Row>
        ),
        size: 200,
      }),
      columnHelper.accessor((r) => r.residualsEarned ?? 0, {
        id: 'residualsEarned',
        header: () => (
          <SortableHeader
            label={t('Residuals.residuals_earned')}
            field='residualsEarned'
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={handleSort}
          />
        ),
        cell: ({ row }) => (
          <Text size='s' weight='light-medium'>
            {formatCurrency(row.original.residualsEarned ?? 0, 'USD')}
          </Text>
        ),
        size: 120,
      }),
      columnHelper.accessor((r) => r.status, {
        id: 'status',
        header: () => (
          <Row gapSize='none'>
            <Text size='s' weight='semi-bold'>
              {t('Residuals.status')}
            </Text>
          </Row>
        ),
        cell: ({ row }) => (
          <ImageWithFallback
            src={getStatusIcon(row.original.status)}
            placeholderSrc={accruingStatusIcon}
          />
        ),
        size: 100,
      }),
      columnHelper.accessor((r) => r.endDate, {
        id: 'endDate',
        header: () => (
          <SortableHeader
            label={t('Residuals.pay_date')}
            field='endDate'
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={handleSort}
          />
        ),
        cell: ({ row }) => (
          <Text size='s' weight='light-medium'>
            {residualsDateFormatter(row.original.endDate ?? '-')}
          </Text>
        ),
        size: 140,
      }),
      columnHelper.accessor((r) => r.datePaid, {
        id: 'datePaid',
        header: () => (
          <SortableHeader
            label={t('Residuals.paid_date')}
            field='datePaid'
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={handleSort}
          />
        ),
        cell: ({ row }) => (
          <Text size='s' weight='light-medium'>
            {residualsDateFormatter(row.original.datePaid ?? '-')}
          </Text>
        ),
        size: 140,
      }),
    ],
    [t, sortBy, sortOrder, handleSort],
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <DataTable
      table={table}
      mobileCardFields={mobileCardFields}
      mobileGetImageUrl={(row) => row.original.logo}
      onRowClick={(row) => {
        const report = row.original;
        const reportId = report.feesReportId || report._id;
        navigate(`${pathname}/${reportId}`, { state: { report } });
      }}
    />
  );
}

export default FinalUserResidualsTable;
